import React, { useCallback, useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { Text } from '@atlaskit/primitives/compiled';

import type { UpdateWorkItemFieldsAction } from '../../../../../../common/types';
import { FieldTooltipContent } from '../../../../../../common/ui/FieldTooltipContent';
import {
	type RegisterTooltipProvider,
	useRegisterTooltipContentProvider,
} from '../../../../../../common/utils/useRegisterTooltipContentProvider';
import {
	formatSecondsToHuman,
	formatSecondsWithSettings,
	parseSuggestionToSeconds,
} from '../utils';

import type { timeTrackingField_updateWorkItemFieldsActionRenderer$key } from './__generated__/timeTrackingField_updateWorkItemFieldsActionRenderer.graphql';
import { getFieldTextCopy, toTimeTrackingFieldValue } from './utils';

const messages = defineMessages({
	originalEstimateLabel: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.remaining-time-estimate-field.original-estimate-label',
		defaultMessage: 'Current original estimate',
		description: 'Label for the original estimate in the remaining time estimate field',
	},
	remainingEstimateLabel: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.remaining-time-estimate-field.remaining-estimate-label',
		defaultMessage: 'Current remaining estimate',
		description: 'Label for the remaining estimate in the remaining time estimate field',
	},
	originalEstimateSuggestedLabel: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.remaining-time-estimate-field.original-estimate-suggested',
		defaultMessage: 'Suggested original estimate',
		description: 'Label for the suggested original estimate in the remaining time estimate field',
	},
	remainingEstimateSuggestedLabel: {
		id: 'update-work-item-fields-action-renderer.ui.action-card.remaining-time-estimate-field.remaining-estimate-suggested',
		defaultMessage: 'Suggested remaining estimate',
		description: 'Label for the suggested remaining estimate in the remaining time estimate field',
	},
});

export type Props = {
	queryRef: timeTrackingField_updateWorkItemFieldsActionRenderer$key;
	action: UpdateWorkItemFieldsAction;
	registerTooltipProvider?: RegisterTooltipProvider;
};

export const TimeTrackingField = ({
	queryRef,
	action,
	registerTooltipProvider,
}: Props): React.JSX.Element | null => {
	const { formatMessage } = useIntl();

	const data = useFragment(
		graphql`
			fragment timeTrackingField_updateWorkItemFieldsActionRenderer on JiraTimeTrackingField {
				fieldId
				name
				timeTrackingSettings {
					isJiraConfiguredTimeTrackingEnabled
					workingHoursPerDay
					workingDaysPerWeek
					defaultFormat
					defaultUnit
				}
				originalEstimate {
					timeInSeconds
				}
				remainingEstimate {
					timeInSeconds
				}
			}
		`,
		queryRef,
	);

	const isFieldRenderable = data?.fieldId === action?.data?.fieldKey;
	const isTimeTrackingEnabled = !!data?.timeTrackingSettings?.isJiraConfiguredTimeTrackingEnabled;
	const shouldRender = isFieldRenderable && isTimeTrackingEnabled;

	const fieldValue = toTimeTrackingFieldValue(action?.data?.fieldValue);
	const suggestedOriginalEstimate = fieldValue?.originalEstimate;
	const suggestedRemainingEstimate = fieldValue?.remainingEstimate;

	const { defaultUnit, defaultFormat, workingHoursPerDay, workingDaysPerWeek } =
		data?.timeTrackingSettings ?? {};

	const normalizedOriginalSeconds = parseSuggestionToSeconds(suggestedOriginalEstimate || '', {
		defaultUnit,
		defaultFormat,
		workingHoursPerDay,
		workingDaysPerWeek,
	});

	const normalizedRemainingSeconds = parseSuggestionToSeconds(suggestedRemainingEstimate || '', {
		defaultUnit,
		defaultFormat,
		workingHoursPerDay,
		workingDaysPerWeek,
	});

	const normalizedOriginalValue = useMemo(
		() =>
			normalizedOriginalSeconds != null
				? formatSecondsWithSettings(normalizedOriginalSeconds, {
						defaultUnit,
						defaultFormat,
						workingHoursPerDay,
						workingDaysPerWeek,
					})
				: suggestedOriginalEstimate,
		[
			normalizedOriginalSeconds,
			defaultUnit,
			defaultFormat,
			workingHoursPerDay,
			workingDaysPerWeek,
			suggestedOriginalEstimate,
		],
	);

	const normalizedRemainingValue = useMemo(
		() =>
			normalizedRemainingSeconds != null
				? formatSecondsWithSettings(normalizedRemainingSeconds, {
						defaultUnit,
						defaultFormat,
						workingHoursPerDay,
						workingDaysPerWeek,
					})
				: suggestedRemainingEstimate,
		[
			normalizedRemainingSeconds,
			defaultUnit,
			defaultFormat,
			workingHoursPerDay,
			workingDaysPerWeek,
			suggestedRemainingEstimate,
		],
	);

	const normalizedCurrentOriginalValue = formatSecondsToHuman(
		data?.originalEstimate?.timeInSeconds ?? null,
	);
	const normalizedCurrentRemainingValue = formatSecondsToHuman(
		data?.remainingEstimate?.timeInSeconds ?? null,
	);

	const toolTipContentProvider = useCallback(() => {
		/*
			The time tracking field has some confusing behaviour when it comes to setting the original and remaining estimates.
			The shape of the object we receive as an action (and send to the API) is as so:
			{
				originalEstimate?: string | nulll   // undefined if doesn't have a value
				remainingEstimate?: string | null   // undefined if doesn't have a value
			}
			* Note that passing in an empty string ('') or null both act as "clearing" the estimate field.

			When we provide a valid value (e.g a string like '7d', '2h', '1w 6d 7h'), the API handles this as we expect it to for
			original and remaining estimates - it updates the value as we would expect.

			However, when we "clear" fields, the API behaves less intuitively. If we pass in an empty string/null, the API call
			will be successful, but the field will not be cleared. Instead it will take the value of the other estimate field
			and use that.

			For example, suppose we have a Jira work item has an original estimate of '7d' and a remaining estimate of '3d'.
			If we send the following object to the API:
			{
				remainingEstimate: '',
			}
			The API will set the remainingEstimate to '7d' now, obtaining this value from the original estimate. The same is true
			if we set originalEstimate to a cleared value.

			However, if we clear both fields by sending the following object to the API:
			{
				originalEstimate: '',
				remainingEstimate: '',
			}
			The API will clear both fields, as we would expect.

			The code below thus handles this behaviour for the suggested estimate values through the following process:
			Suppose we have a Jira work item has an original estimate of '7d' and a remaining estimate of '3d'.
			1) If the estimate is undefined, return the undefined object (this isn't going to be changed/rendered)
			Example in originalEstimate scope
			{
				remainingEstimate: '7d',
			}
			suggestedValue = undefined

			2) If the estimate is truthy (i.e not an empty string/null), we use that value
			Example in originalEstimate scope
			{
				originalEstimate: '5d',
			}
			suggestedValue = '5d'

			3) If the other estimate is truthy, we will use the suggested value of the other estimate field.
			Example in originalEstimate scope
			{
				originalEstimate: ''
				remainingEstimate: '5d'
			}
			suggestedValue = '5d'

			4) If that is not available, (i.e the current estimate is falsy AND the other estimate is falsy), we use the current value of the other estimate field.
			Example in originalEstimate scope
			{
				originalEstimate: ''
			}
			suggestedValue = '3d'

			* Note: 3) and 4) are the same logic as the BE code: https://atlassian.slack.com/archives/C092WQZSBSP/p1762146479675429?thread_ts=1761885651.797099&cid=C092WQZSBSP
			There is also the case where if both estimates are cleared, then both suggested values will be null so we don't render any suggested
			values as we are clearing the fields.
		*/
		const getSuggestedOriginal = () => {
			if (suggestedOriginalEstimate === undefined) {
				return suggestedOriginalEstimate;
			}
			if (suggestedOriginalEstimate) {
				return normalizedOriginalValue;
			}
			return suggestedRemainingEstimate
				? normalizedRemainingValue
				: normalizedCurrentRemainingValue;
		};

		const getSuggestedRemaining = () => {
			if (suggestedRemainingEstimate === undefined) {
				return suggestedRemainingEstimate;
			}
			if (suggestedRemainingEstimate) {
				return normalizedRemainingValue;
			}
			return suggestedOriginalEstimate ? normalizedOriginalValue : normalizedCurrentOriginalValue;
		};

		// Check if both estimates are explicitly cleared (empty/null, not undefined)
		const originalAndRemainingCleared =
			suggestedOriginalEstimate !== undefined &&
			!suggestedOriginalEstimate &&
			suggestedRemainingEstimate !== undefined &&
			!suggestedRemainingEstimate;

		// If both are cleared, show null. Otherwise, compute the suggested values.
		const suggestedOriginal = originalAndRemainingCleared ? null : getSuggestedOriginal();
		const suggestedRemaining = originalAndRemainingCleared ? null : getSuggestedRemaining();

		return (
			<FieldTooltipContent
				reasoning={action?.data?.reasoning}
				fieldName={data?.name}
				additionalSuggestions={[
					{
						suggested: suggestedOriginalEstimate !== undefined && normalizedCurrentOriginalValue,
						label: formatMessage(messages.originalEstimateLabel),
						testIdSuffix: 'original-estimate-current',
					},
					{
						suggested: suggestedRemainingEstimate !== undefined && normalizedCurrentRemainingValue,
						label: formatMessage(messages.remainingEstimateLabel),
						testIdSuffix: 'remaining-estimate-current',
					},
					{
						suggested: suggestedOriginal,
						label: formatMessage(messages.originalEstimateSuggestedLabel),
						testIdSuffix: 'original-estimate-suggested',
					},
					{
						suggested: suggestedRemaining,
						label: formatMessage(messages.remainingEstimateSuggestedLabel),
						testIdSuffix: 'remaining-estimate-suggested',
					},
				]}
			/>
		);
	}, [
		action?.data?.reasoning,
		data?.name,
		suggestedOriginalEstimate,
		suggestedRemainingEstimate,
		normalizedOriginalValue,
		normalizedRemainingValue,
		normalizedCurrentOriginalValue,
		normalizedCurrentRemainingValue,
		formatMessage,
	]);

	useRegisterTooltipContentProvider({
		registerTooltipProvider,
		fieldId: action?.data?.fieldKey,
		provider: toolTipContentProvider,
		enabled: shouldRender,
	});

	if (!shouldRender) {
		return null;
	}

	const value = getFieldTextCopy({
		suggestedOriginalEstimate,
		suggestedRemainingEstimate,
		normalizedOriginalValue,
		normalizedRemainingValue,
		normalizedCurrentOriginalValue,
		normalizedCurrentRemainingValue,
		formatMessage,
	});

	return (
		<Text
			color="color.text.subtle"
			maxLines={1}
			testId="update-work-item-fields-action-renderer.ui.action-card.card-fields-list.remaining-time-estimate-field"
		>
			{value}
		</Text>
	);
};
